import { Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './components/auth/Login';
import {
  ProtectedRoute,
  GlobalAdminRoute,
  TenantAdminRoute,
} from './components/auth/ProtectedRoute';
import { Dashboard } from './components/dashboard/Dashboard';
import { BotList } from './components/bots/BotList';
import { BotConfig } from './components/bots/BotConfig';
import { BotCreate } from './components/bots/BotCreate';
import { VoiceList } from './components/voices/VoiceList';
import { VoiceClone } from './components/voices/VoiceClone';
import { TenantManagement } from './components/admin/TenantManagement';
import { UserManagement } from './components/admin/UserManagement';
import { LiteLLMEndpoints } from './components/admin/LiteLLMEndpoints';
import { HeadscaleAdmin } from './components/admin/HeadscaleAdmin';
import { RegistrationTokens } from './components/admin/RegistrationTokens';
import { GPUManagement } from './components/admin/GPUManagement';
import { SearXNGSettings } from './components/admin/SearXNGSettings';
import { NodeDashboard } from './components/monitoring/NodeDashboard';
import { CostReports } from './components/monitoring/CostReports';
import { SystemMonitoring } from './components/monitoring/SystemMonitoring';
import { AuditLog } from './components/reports/AuditLog';
import { UsageReports } from './components/reports/UsageReports';

// =============================================================================
// Placeholder pages
// =============================================================================

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-discord-blurple mb-4">404</h1>
        <p className="text-xl text-gray-400">Page not found</p>
      </div>
    </div>
  );
}

// =============================================================================
// App Component
// =============================================================================

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected dashboard routes - any authenticated user */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Protected bot management routes - any authenticated user */}
      <Route
        path="/bots"
        element={
          <ProtectedRoute>
            <BotList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots/new"
        element={
          <ProtectedRoute>
            <BotCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bots/:id/config"
        element={
          <ProtectedRoute>
            <BotConfig />
          </ProtectedRoute>
        }
      />

      {/* Protected voice profile routes - any authenticated user */}
      <Route
        path="/voices"
        element={
          <ProtectedRoute>
            <VoiceList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/voices/new"
        element={
          <ProtectedRoute>
            <VoiceClone />
          </ProtectedRoute>
        }
      />

      {/* Admin routes - requires GLOBAL_ADMIN role */}
      <Route
        path="/admin/tenants"
        element={
          <GlobalAdminRoute>
            <TenantManagement />
          </GlobalAdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <TenantAdminRoute>
            <UserManagement />
          </TenantAdminRoute>
        }
      />
      <Route
        path="/admin/litellm"
        element={
          <TenantAdminRoute>
            <LiteLLMEndpoints />
          </TenantAdminRoute>
        }
      />

      {/* Admin routes - requires TENANT_ADMIN or higher */}
      <Route
        path="/admin/nodes"
        element={
          <TenantAdminRoute>
            <NodeDashboard />
          </TenantAdminRoute>
        }
      />
      <Route
        path="/admin/headscale"
        element={
          <GlobalAdminRoute>
            <HeadscaleAdmin />
          </GlobalAdminRoute>
        }
      />
      <Route
        path="/admin/registration-tokens"
        element={
          <GlobalAdminRoute>
            <RegistrationTokens />
          </GlobalAdminRoute>
        }
      />
      <Route
        path="/admin/gpus"
        element={
          <GlobalAdminRoute>
            <GPUManagement />
          </GlobalAdminRoute>
        }
      />
      <Route
        path="/admin/searxng"
        element={
          <GlobalAdminRoute>
            <SearXNGSettings />
          </GlobalAdminRoute>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <TenantAdminRoute>
            <AuditLog />
          </TenantAdminRoute>
        }
      />

      {/* Monitoring routes - requires TENANT_ADMIN or higher */}
      <Route
        path="/monitoring"
        element={
          <TenantAdminRoute>
            <SystemMonitoring />
          </TenantAdminRoute>
        }
      />

      {/* Protected reports routes - any authenticated user */}
      <Route
        path="/reports/usage"
        element={
          <ProtectedRoute>
            <UsageReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/costs"
        element={
          <ProtectedRoute>
            <CostReports />
          </ProtectedRoute>
        }
      />

      {/* Redirect root to dashboard (will redirect to login if not authenticated) */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
